import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { CashRegisterNav } from '@/components/CashRegister/CashRegisterNav'
import { EetWarningBanner } from '@/components/Eet/EetWarningBanner'
import { PageHeader } from '@/components/ds'
import { ErrorState } from '@/components/ErrorState'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { CashRegisterSettingsForm, CashRegisterSettingsResponse } from '@/forms/CashRegisterSettingsForm/CashRegisterSettingsForm'
import { EetSettingsSection } from '@/forms/EetSettingsForm/EetSettingsForm'
import { EetStatus } from '@invo.space/shared'

export default function CashRegisterSettingsPage() {
  const { t } = useTranslation(undefined, { keyPrefix: 'cashRegister' })

  const { data, error, isLoading } = useSWR<CashRegisterSettingsResponse>('/api/v1/cash-register/settings', apiFetcher)
  const { data: eetStatus, mutate: mutateEetStatus } = useSWR<EetStatus>('/api/v2/eet', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [
      { href: '/cash-register', children: 'Pokladna' },
      { href: '/cash-register/settings', children: 'Nastavení' },
    ],
  })

  return (
    <>
      <title>Nastavení pokladny</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <EetWarningBanner />
        <CashRegisterNav className="mb-5" />

        <PageHeader title="Nastavení pokladny" description="Údaje na účtence, způsoby platby, sazby DPH a sklad, ze kterého pokladna prodává." />

        {isLoading && (
          <div className="max-w-5xl space-y-4">
            <Skeleton className="h-64 rounded-lg" />
            <Skeleton className="h-64 rounded-lg" />
          </div>
        )}

        {error && <ErrorState>{t('error')}</ErrorState>}

        {!isLoading && !error && (
          <Tabs defaultValue="general" variant="outline" className="max-w-5xl">
            <TabsList>
              <TabsTrigger value="general">{t('settings.tabs.general')}</TabsTrigger>
              {eetStatus?.isCzech && <TabsTrigger value="eet">{t('settings.tabs.eet')}</TabsTrigger>}
            </TabsList>

            <TabsContent value="general">
              <CashRegisterSettingsForm settings={data} />
            </TabsContent>

            {eetStatus?.isCzech && (
              <TabsContent value="eet">
                <EetSettingsSection status={eetStatus} onSuccess={() => mutateEetStatus()} />
              </TabsContent>
            )}
          </Tabs>
        )}
      </div>
    </>
  )
}
